/**
 * Supabase client factory. Apps inject url + anon key (both public,
 * RLS-protected); the service_role key NEVER reaches a client (doc 00 §13.2).
 */

import type { Database } from '@gym/db-types';
import type { AppMetadataClaims } from '@gym/shared';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export type GymSupabaseClient = SupabaseClient<Database>;

export interface CreateGymClientOptions {
  url: string;
  anonKey: string;
  /** Optional access token to seed the session (e.g. SSR / native restore). */
  accessToken?: string;
}

/** Create a typed Supabase client bound to the gym Database schema. */
export function createGymClient(options: CreateGymClientOptions): GymSupabaseClient {
  const { url, anonKey, accessToken } = options;
  return createClient<Database>(url, anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
    },
    global: accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {},
  });
}

/**
 * Read the verified app_metadata claims from the current session. Claims are
 * minted server-side by the access-token hook (never client-writable); clients
 * read role/tenant from here, NOT by re-querying the DB per render (plan 02b §3.8).
 */
export async function getClaims(client: GymSupabaseClient): Promise<AppMetadataClaims | null> {
  const { data } = await client.auth.getSession();
  const am = data.session?.user.app_metadata as Record<string, unknown> | undefined;
  if (!am || typeof am['tenant_id'] !== 'string' || typeof am['app_role'] !== 'string') return null;
  return {
    tenant_id: am['tenant_id'],
    app_role: am['app_role'] as AppMetadataClaims['app_role'],
    staff_id: typeof am['staff_id'] === 'string' ? am['staff_id'] : null,
    member_id: typeof am['member_id'] === 'string' ? am['member_id'] : null,
  };
}
